/**
 * Human Resources · reports.
 *
 * Pick a report, narrow it (department, employment, Saudi or not, period),
 * group it where it can be grouped, and take it the way you need it: a table
 * on screen, a chart, a CSV file for Excel, or a printout. A row that names a
 * person opens their file. Reports that show pay or confidential records are
 * offered only to those allowed to see them.
 */
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useHrAttendanceRange, useHrCost, useHrPeople, useHrRecords, useHrRules } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Field, Input, Segmented, Select, Skeleton, Table } from "../../design/components";
import { mergeRules } from "../../domain/hr";
import { buildReport, GROUPS, REPORTS, toCsv, type GroupBy, type ReportFilter } from "../../domain/hrReports";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { saveFile } from "../../lib/saveFile";
import { useHrAccess } from "./access";
import { addDays, escapeHtml, fileLink, h, PreviewBar, printHtml, todayIso } from "./kit";
import { ColumnChart, s, SampleBanner, SUITE } from "./shared";

type View = "table" | "chart";

export function HrReportsPage() {
  const session = useSession();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can } = useHrAccess();
  const available = REPORTS.filter((r) => !r.needs || (r.needs === "pay" ? can.pay : can.confidential));
  const key = available.some((r) => r.key === params.get("report"))
    ? (params.get("report") as string)
    : available[0]?.key;
  const def = available.find((r) => r.key === key);
  const [groupBy, setGroupBy] = useState<GroupBy>("department");
  const [view, setView] = useState<View>("table");
  const [f, setF] = useState<ReportFilter>({
    from: addDays(todayIso(), -30),
    to: todayIso(),
    department: "",
    employment: "",
    saudi: "",
  });
  const [saved, setSaved] = useState<string | null>(null);

  const people = useHrPeople();
  const records = useHrRecords();
  const rules = useHrRules().data?.rules ?? mergeRules(null);
  const attendance = useHrAttendanceRange(f.from, f.to);
  const cost = useHrCost();
  const loading = people.isLoading || records.isLoading;

  const report = useMemo(() => {
    if (!people.data || !key) return null;
    return buildReport(
      key,
      {
        people: people.data.employees,
        records: records.data?.records ?? [],
        attendance: attendance.data ?? [],
        cost: can.pay ? (cost.data ?? null) : null,
        rules,
        on: new Date(),
        can,
      },
      f,
      groupBy,
    );
  }, [key, people.data, records.data, attendance.data, cost.data, rules, f, groupBy, can]);

  useScreenContext({
    route: "/hr-reports",
    title: "Reports",
    summary: [t("{n} reports, shown as a table or chart, saved as CSV or printed.", { n: available.length })],
    offers: [{ label: t("Headcount by department"), ask: "what is the headcount by department?" }],
    worksInFull: true,
  });

  const title = def ? t(def.label) : "";
  const csv = async () => {
    if (!report) return;
    const outcome = await saveFile(
      `${(def?.label ?? "report").replace(/\W+/g, "-")}-${todayIso()}.csv`,
      toCsv(report, t),
      "text/csv",
    );
    setSaved(outcome === "saved" ? t("Saved as a CSV file — it opens in Excel.") : null);
  };
  const print = () => {
    if (!report) return;
    const head = `<div class="head"><span>${escapeHtml(session.tenant ?? "")}</span><span>${escapeHtml(todayIso())}</span></div><h1>${escapeHtml(title)}</h1>`;
    const table = `<table><thead><tr>${report.columns.map((c) => `<th>${escapeHtml(t(c.label))}</th>`).join("")}</tr></thead><tbody>${report.rows
      .map((r) => `<tr>${report.columns.map((c) => `<td>${escapeHtml(String(r[c.key] ?? ""))}</td>`).join("")}</tr>`)
      .join(
        "",
      )}${report.total ? `<tr>${report.columns.map((c) => `<th>${escapeHtml(String(report.total?.[c.key] ?? ""))}</th>`).join("")}</tr>` : ""}</tbody></table>`;
    void printHtml(title, head + table);
  };

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Reports">
      <PreviewBar />
      <SampleBanner />
      <Card title={t("1. Choose a report")}>
        <div className={h.actions}>
          {available.map((r) => (
            <button
              key={r.key}
              type="button"
              className={h.action}
              aria-pressed={r.key === key}
              style={r.key === key ? { borderColor: "var(--blue)", boxShadow: "0 0 0 1px var(--blue)" } : undefined}
              onClick={() => setParams({ report: r.key }, { replace: true })}
              data-testid={`hr-report-${r.key}`}
            >
              <span>
                {t(r.label)}
                <small>{t(r.what)}</small>
              </span>
            </button>
          ))}
        </div>
      </Card>
      {def && (
        <Card title={t("2. Narrow it down")}>
          <div className={s.filters}>
            <Field label="Department">
              <Select value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })}>
                <option value="">{t("All departments")}</option>
                {(people.data?.departments ?? []).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Employment">
              <Select
                value={f.employment}
                onChange={(e) => setF({ ...f, employment: e.target.value as ReportFilter["employment"] })}
              >
                <option value="">{t("Direct and outsourced")}</option>
                <option value="direct">{t("Direct")}</option>
                <option value="outsourced">{t("Outsourced")}</option>
              </Select>
            </Field>
            <Field label="Nationality">
              <Select value={f.saudi} onChange={(e) => setF({ ...f, saudi: e.target.value as ReportFilter["saudi"] })}>
                <option value="">{t("Everyone")}</option>
                <option value="saudi">{t("Saudi")}</option>
                <option value="non">{t("Non-Saudi")}</option>
              </Select>
            </Field>
            {def.period && (
              <>
                <Field label="From">
                  <Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
                </Field>
                <Field label="To">
                  <Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
                </Field>
              </>
            )}
            {def.grouped && (
              <Field label="Group by">
                <Select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                  data-testid="hr-report-group"
                >
                  {GROUPS.map((g) => (
                    <option key={g.key} value={g.key}>
                      {t(g.label)}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
        </Card>
      )}
      {loading && <Skeleton rows={8} />}
      {report && def && (
        <Card
          title={`3. ${title}`}
          sub={t("{n} rows", { n: report.rows.length })}
          actions={
            <span className={h.statusBtns}>
              {report.chart && (
                <Segmented
                  value={view}
                  options={[
                    { value: "table", label: "Table" },
                    { value: "chart", label: "Chart" },
                  ]}
                  onChange={setView}
                  ariaLabel="Show as"
                />
              )}
              <Button onClick={() => void csv()} data-testid="hr-report-csv">
                ⬇ {t("CSV for Excel")}
              </Button>
              <Button onClick={print} data-testid="hr-report-print">
                🖨 {t("Print or PDF")}
              </Button>
            </span>
          }
          testId="hr-report"
        >
          {saved && <p className={s.ok}>{saved}</p>}
          {report.rows.length === 0 ? (
            <EmptyState>{t("Nothing to report for these choices.")}</EmptyState>
          ) : view === "chart" && report.chart ? (
            <ColumnChart
              label={title}
              format={(v) => String(Math.round(v))}
              data={report.rows.slice(0, 16).map((r, i) => ({
                key: String(i),
                label: String(r[report.columns[0].key] ?? r.name ?? "").slice(0, 12),
                value: Number(r[report.chart as string] ?? 0),
              }))}
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  {report.columns.map((c) => (
                    <th key={c.key} className={c.num ? s.num : undefined}>
                      {t(c.label)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r, i) => (
                  <tr
                    key={i}
                    style={r.employeeId ? { cursor: "pointer" } : undefined}
                    onClick={() => r.employeeId && nav(fileLink(String(r.employeeId)))}
                    data-testid="hr-report-row"
                  >
                    {report.columns.map((c) => (
                      <td key={c.key} className={c.num ? s.num : undefined}>
                        {c.key === report.personKey && r.employeeId ? (
                          <a href={`#${fileLink(String(r.employeeId))}`} onClick={(e) => e.preventDefault()}>
                            {String(r[c.key] ?? "")}
                          </a>
                        ) : typeof r[c.key] === "number" ? (
                          (r[c.key] as number).toLocaleString("en")
                        ) : (
                          t(String(r[c.key] ?? "—"))
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {report.total && (
                  <tr>
                    {report.columns.map((c) => (
                      <td key={c.key} className={c.num ? s.num : undefined}>
                        <b>
                          {typeof report.total?.[c.key] === "number"
                            ? (report.total[c.key] as number).toLocaleString("en")
                            : t(String(report.total?.[c.key] ?? ""))}
                        </b>
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </Table>
          )}
          {view === "chart" && report.rows.length > 16 && (
            <p className={s.note}>{t("The chart shows the first 16 rows.")}</p>
          )}
        </Card>
      )}
    </Page>
  );
}
